import { useEffect, useState } from "react";
import { Zap } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./ui/dialog";
import { useAuth } from "../context/AuthContext";

const Section = ({ title, cls, items, render, testid }) => (
  <div data-testid={testid}>
    <div className={`text-xs font-semibold mb-1 ${cls}`}>{title} ({items.length})</div>
    {items.length ? <div className="text-xs leading-relaxed text-foreground/90 max-h-40 overflow-y-auto rounded border border-border p-2">{items.map(render).join(", ")}</div>
      : <div className="text-xs text-muted-foreground">—</div>}
  </div>
);

export function ApplyTimeLimitButton() {
  const { user: me } = useAuth();
  const [busy, setBusy] = useState(false);
  const [prog, setProg] = useState("");
  const [result, setResult] = useState(null);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (me?.role !== "admin") return;
    api.get("/vouchers/apply-time-limit-all/status").then((r) => { if (r.data.status === "done") setResult(r.data); }).catch(() => {});
  }, [me?.role]);
  if (me?.role !== "admin") return null;

  const run = async () => {
    if (!window.confirm("Terapkan batas waktu (limit-uptime + masa berlaku sejak login pertama) ke SEMUA router online yang belum diterapkan? Proses berjalan di latar belakang.")) return;
    setBusy(true);
    const t0 = Date.now();
    try {
      const { data: start } = await api.post("/vouchers/apply-time-limit-all");
      toast.info(start.already_running ? "Proses massal masih berjalan — menunggu selesai..." : "Terapkan batas waktu massal dimulai (300 router bisa memakan 1-3 menit)...");
      const poll = async () => {
        try {
          const { data } = await api.get("/vouchers/apply-time-limit-all/status");
          if (data.status === "running") setProg(`${data.done ?? 0}/${data.total ?? "?"}`);
          if (data.status === "done" && new Date(data.finished_at).getTime() >= t0 - 5000) {
            setBusy(false); setResult(data); setOpen(true);
            toast.success(`Selesai: ${data.ok} router diterapkan, ${data.already ?? 0} sudah sebelumnya, ${data.offline ?? 0} offline`, { duration: 8000 });
            return;
          }
        } catch { /* coba lagi */ }
        if (Date.now() - t0 < 600000) setTimeout(poll, 4000);
        else setBusy(false);
      };
      setTimeout(poll, 4000);
    } catch (e) { toast.error(errMsg(e)); setBusy(false); }
  };

  return (
    <>
      <div className="inline-flex gap-1">
        <Button variant="outline" onClick={run} disabled={busy} data-testid="btn-apply-time-limit" title="Set limit-uptime + masa berlaku di MikroTik untuk SEMUA router (khusus Super Admin)">
          <Zap className={`w-4 h-4 mr-1.5 ${busy ? "animate-pulse" : ""}`} /> {busy ? `Menerapkan ${prog || "..."} router` : "Terapkan Batas Waktu (Semua)"}
        </Button>
        {result && <Button variant="ghost" size="sm" onClick={() => setOpen(true)} data-testid="btn-time-limit-result" title="Lihat cabang mana saja yang sudah diterapkan">Hasil</Button>}
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl" data-testid="dialog-time-limit-result">
          <DialogHeader>
            <DialogTitle className="text-base font-heading">Hasil Terapkan Batas Waktu</DialogTitle>
            <DialogDescription className="text-xs">Terakhir dijalankan {result?.finished_at ? new Date(result.finished_at).toLocaleString("id-ID") : "-"} oleh {result?.triggered_by || "-"} — {result?.fixed ?? 0} voucher diset limit-uptime, {result?.profiles_fixed ?? 0} profile diberi/diperbarui masa berlaku, {result?.scheduled ?? 0} voucher lama dijadwalkan expired, {result?.removed ?? 0} voucher habis dihapus, {result?.hs_mon ?? 0} scheduler monitor hotspot & {result?.ppp_sched ?? 0} scheduler isolir PPPoE dipasang, {result?.ppp_stamped ?? 0} secret distempel jatuh tempo. Otomatis diulang tiap hari 03:15 WIB (router offline ikut tersusul).</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Section title="Diterapkan sekarang" cls="text-emerald-500" testid="tl-applied" items={result?.applied_routers || []} render={(x) => `${x.router} (${x.fixed} vcr limit, ${x.profiles} profile, ${x.scheduled ?? 0} dijadwalkan, ${x.removed ?? 0} dihapus, ${x.hs_mon ?? 0} mon-hotspot, ${x.ppp_sched ?? 0} sched-pppoe, ${x.ppp_stamped ?? 0} secret distempel)`} />
            <Section title="Sudah diterapkan sebelumnya" cls="text-sky-500" testid="tl-already" items={result?.already_routers || []} render={(x) => x} />
            <Section title="Offline — dilewati, ulangi saat online" cls="text-rose-500" testid="tl-offline" items={result?.offline_routers || []} render={(x) => x} />
            <Section title="Tanpa paket Batas Waktu — tidak perlu" cls="text-muted-foreground" testid="tl-skipped" items={result?.skipped_routers || []} render={(x) => x} />
            <Section title="Gagal login/API" cls="text-amber-500" testid="tl-errors" items={result?.errors || []} render={(x) => `${x.router}: ${x.error}`} />
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
